import React, { useId, useMemo } from 'react';
import { addWeeks, format } from 'date-fns';

import { CHART, areaPath, chartPoints, curvePath, tickPositions } from './chartGeometry';
import { ChartRoot } from './ProgressChart.styles';
import { ProgressChartConfig, resolveChartConfig } from './quizTypes';
import { usePipedText } from './usePipedText';

interface ProgressChartProps {
  data: { blockConfig?: Partial<ProgressChartConfig> };
}

const MAX_WEEK_TICKS = 6;

interface BadgeProps {
  x: number;
  y: number;
  label: string;
  goal?: boolean;
}

const ChartBadge: React.FC<BadgeProps> = ({ x, y, label, goal }) => {
  const width = Math.max(36, label.length * 6.6 + 16);
  const left = Math.min(CHART.width - width, Math.max(0, x - width / 2));
  return (
    <g className={`chart-badge${goal ? ' is-goal' : ''}`}>
      <rect x={left} y={y - 30} width={width} height={20} rx={6} />
      <text x={left + width / 2} y={y - 16} textAnchor="middle">
        {label}
      </text>
    </g>
  );
};

const ProgressChart: React.FC<ProgressChartProps> = ({ data }) => {
  const config = useMemo(() => resolveChartConfig(data?.blockConfig), [data?.blockConfig]);
  const piped = usePipedText().html;
  const gradientId = `chart-area-${useId().replace(/:/g, '')}`;

  const weeks = Math.max(1, Math.round(Number(config.weeks) || 1));
  const goalDate = format(addWeeks(new Date(), weeks), 'MMMM d, yyyy');

  const tickCount = Math.min(weeks, MAX_WEEK_TICKS) + 1;
  const ticks = tickPositions(tickCount).map((x, i) => ({
    x,
    label:
      i === 0
        ? format(new Date(), 'MMM d')
        : i === tickCount - 1
          ? format(addWeeks(new Date(), weeks), 'MMM d')
          : `Week ${Math.round((weeks * i) / (tickCount - 1))}`,
  }));

  const { start, goal } = chartPoints;

  return (
    <ChartRoot className="quiz-block">
      {config.title && (
        <h2 className="quiz-title" dangerouslySetInnerHTML={{ __html: piped(config.title) }} />
      )}
      {config.subtitle && (
        <p className="quiz-subtitle" dangerouslySetInnerHTML={{ __html: piped(config.subtitle) }} />
      )}
      {config.showGoalDate && <p className="chart-goal-date">{goalDate}</p>}

      <div className="quiz-card chart-card">
        <svg
          className="chart-svg"
          viewBox={`0 0 ${CHART.width} ${CHART.height}`}
          role="img"
          aria-label={`${config.startLabel} to ${config.goalLabel} by ${goalDate}`}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop className="chart-area-stop" offset="0%" stopOpacity="0.28" />
              <stop className="chart-area-stop" offset="100%" stopOpacity="0" />
            </linearGradient>
          </defs>

          {[CHART.top, (CHART.top + CHART.bottom) / 2, CHART.bottom].map((y) => (
            <line key={y} className="chart-grid" x1={CHART.left} x2={CHART.right} y1={y} y2={y} />
          ))}

          <path className="chart-area" d={areaPath()} fill={`url(#${gradientId})`} />
          <path className="chart-line" d={curvePath()} />

          <circle className="chart-dot-start" cx={start.x} cy={start.y} r={7} />
          <circle className="chart-dot-goal" cx={goal.x} cy={goal.y} r={8} />

          {config.startLabel && <ChartBadge x={start.x} y={start.y} label={config.startLabel} />}
          {config.goalLabel && <ChartBadge x={goal.x} y={goal.y} label={config.goalLabel} goal />}

          {ticks.map((tick, i) => (
            <text
              key={tick.x}
              className="chart-tick"
              x={tick.x}
              y={CHART.bottom + 24}
              textAnchor={i === 0 ? 'start' : i === ticks.length - 1 ? 'end' : 'middle'}
            >
              {tick.label}
            </text>
          ))}
        </svg>
        {config.footnote && <p className="chart-footnote">{config.footnote}</p>}
      </div>
    </ChartRoot>
  );
};

export default ProgressChart;
